// ─────────────────────────────────────────────────────────────────────────────
// dateFmt — consistent date display helpers for the SFM frontend.
//
// Replaces the ad-hoc patterns scattered across the codebase:
//   new Date(iso).toISOString().slice(0, 10)
//   new Date(iso).toLocaleDateString(undefined, { ... })
//   String(iso).slice(0, 10)
//   etc.
//
// All helpers gracefully handle null/undefined/empty/malformed inputs by
// returning the requested fallback (default "—") rather than NaN/Invalid Date.
//
// Audit-day defense: consistent date formatting across every page means
// an auditor sees the same date string for the same instant on every screen.
// Reduces cognitive load + eliminates the "is this UTC or local?" question.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Today's calendar date "YYYY-MM-DD" in the user's own time zone.
 * Use for: default values of date inputs the user is recording (date raised,
 * date received, date verified). toISOString() gives the UTC date, which is
 * already tomorrow from 8 pm Eastern (7 pm in winter), so a record entered in
 * the evening was stamped with the next day's date.
 *
 * @param {Date} [now=new Date()]
 * @returns {string}
 */
export function todayLocalISO(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** True if value is parseable as a Date with a finite timestamp. */
function isValidDateInput(v) {
  if (!v && v !== 0) return false;
  const d = new Date(v);
  return d instanceof Date && !isNaN(d.getTime());
}

/**
 * ISO date "YYYY-MM-DD" — UTC truncation.
 * Use for: audit-trail rows, document upload dates, due-date columns.
 *
 * @param {string|number|Date|null} value
 * @param {string} [fallback="—"]
 * @returns {string}
 */
export function fmtDateISO(value, fallback = "—") {
  if (!isValidDateInput(value)) return fallback;
  return new Date(value).toISOString().slice(0, 10);
}

/**
 * ISO datetime "YYYY-MM-DD HH:MM:SS UTC" — UTC datetime for audit-trail
 * display. Includes seconds because audit-log entries within the same
 * minute need to be ordered (V-03 immutability is per-row, but readers
 * still want to see the chronological order at a glance).
 *
 * @param {string|number|Date|null} value
 * @param {string} [fallback="—"]
 * @returns {string}
 */
export function fmtDateTimeUTC(value, fallback = "—") {
  if (!isValidDateInput(value)) return fallback;
  return new Date(value).toISOString().replace("T", " ").slice(0, 19) + " UTC";
}

/**
 * Locale-friendly short date: "May 5, 2026"
 * Use for: human-readable display on detail pages.
 *
 * @param {string|number|Date|null} value
 * @param {string} [fallback="—"]
 * @returns {string}
 */
export function fmtDateLong(value, fallback = "—") {
  if (!isValidDateInput(value)) return fallback;
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return fmtDateISO(value, fallback);
  }
}

/**
 * Relative time: "2 days ago", "in 3 hours", "just now"
 * Use for: activity feeds, recent-changes indicators.
 *
 * @param {string|number|Date|null} value
 * @param {string} [fallback="—"]
 * @returns {string}
 */
export function fmtRelative(value, fallback = "—") {
  if (!isValidDateInput(value)) return fallback;
  const now = Date.now();
  const then = new Date(value).getTime();
  const diffMs = now - then;
  const absMs = Math.abs(diffMs);
  const past = diffMs >= 0;

  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;

  let amount, unit;
  if (absMs < minute) {
    return past ? "just now" : "in a moment";
  } else if (absMs < hour) {
    amount = Math.round(absMs / minute);
    unit = amount === 1 ? "minute" : "minutes";
  } else if (absMs < day) {
    amount = Math.round(absMs / hour);
    unit = amount === 1 ? "hour" : "hours";
  } else if (absMs < week) {
    amount = Math.round(absMs / day);
    unit = amount === 1 ? "day" : "days";
  } else {
    // Beyond a week, prefer absolute date — relative loses meaning
    return fmtDateISO(value, fallback);
  }
  return past ? `${amount} ${unit} ago` : `in ${amount} ${unit}`;
}

/**
 * Smart-format: relative if within the last 7 days, otherwise ISO.
 * Use for: list rows where recent items want "2 hours ago" but old items
 * want a date stamp.
 *
 * @param {string|number|Date|null} value
 * @param {string} [fallback="—"]
 * @returns {string}
 */
export function fmtSmart(value, fallback = "—") {
  if (!isValidDateInput(value)) return fallback;
  const ageMs = Date.now() - new Date(value).getTime();
  const week = 7 * 24 * 60 * 60 * 1000;
  if (ageMs >= 0 && ageMs < week) return fmtRelative(value, fallback);
  return fmtDateISO(value, fallback);
}

export default {
  fmtDateISO,
  fmtDateTimeUTC,
  fmtDateLong,
  fmtRelative,
  fmtSmart,
};
